import BigNumber from 'bignumber.js';
import RfqsStore from '#/unified-rfqs/store/rfqs';
import { isActiveRfq, Rfq } from '#/unified-rfqs/entities/rfq';
import { AuctionType } from '#/unified-rfqs/entities/shared';
import { ListRfqsReq } from '#/unified-rfqs/repositories/grfq-types';
import { FormOption } from '#/unified-rfqs/ui/rfq-builder/types';
import { GrfqService } from '#/unified-rfqs/services/grfq';
import { MergedSourcesFetcher } from '#/unified-rfqs/services/merged-sources-fetcher';
import { DrfqService } from '#/unified-rfqs/services/drfq';
import PublicStore from '#/unified-rfqs/store/public-store';
import { Instrument } from '#/unified-rfqs/entities/drfq/instruments';
import { calcDrfqNotional } from '#/unified-rfqs/entities/drfq/domain';

type AuctionTypeToRfqTypeMap = { [S in AuctionType]: Rfq['type'] };

const AuctionToRfqMap: AuctionTypeToRfqTypeMap = {
  OB: 'grfq',
  RFQ: 'drfq',
};

function hasStrategy(rfq: Rfq, strategies: readonly string[]): boolean {
  if (strategies.length > 0) {
    return strategies.includes(rfq.strategy_code);
  }
  return true;
}

function hasProduct(rfq: Rfq, productCodes: readonly string[]): boolean {
  if (productCodes.length > 0) {
    return rfq.product_codes.some((code) => productCodes.includes(code));
  }

  return true;
}

function hasCounterparty(
  rfq: Rfq,
  counterparties?: readonly string[],
): boolean {
  if (!counterparties?.length) return true;
  if (rfq.type !== 'drfq') return true;
  if (rfq.is_taker_anonymous) return false;
  if (rfq.taker_desk_name == null) return false;

  return counterparties.includes(rfq.taker_desk_name);
}

function hasAuctionType(
  rfq: Rfq,
  auctionTypes: readonly FormOption<AuctionType>[],
): boolean {
  if (auctionTypes.length === 0) return true;
  return auctionTypes.some((type) => AuctionToRfqMap[type.value] === rfq.type);
}

function isWithinNotionalRange(
  rfq: Rfq,
  notional: { min: string; max: string },
  drfqFutureInstruments: readonly Instrument[],
): boolean {
  if (rfq.type !== 'drfq') return true;
  if (drfqFutureInstruments.length === 0) return true;

  const minNotional = BigNumber(notional.min);
  const maxNotional = BigNumber(notional.max);
  if (minNotional.isNaN() && maxNotional.isNaN()) return true;

  const rfqNotional = calcDrfqNotional(rfq, drfqFutureInstruments);
  if (rfqNotional.isNaN() || rfqNotional.isZero()) return true;

  return (
    (minNotional.isNaN() || rfqNotional.gte(minNotional)) &&
    (maxNotional.isNaN() || rfqNotional.lte(maxNotional))
  );
}

interface RfqsServiceDeps {
  rfqsStore: RfqsStore;
  drfqService: DrfqService;
  grfqService: GrfqService;
  filters: FiltersDeps;
}

interface FiltersDeps {
  auctionTypes: readonly FormOption<AuctionType>[];
  productCodes: readonly string[];
  strategyCodes: readonly string[];
  counterparties?: readonly string[];
  notional: { min: string; max: string };
}

export default class UnifiedRfqsListService extends PublicStore<Rfq> {
  public fetcher: MergedSourcesFetcher<ListRfqsReq, Rfq>;
  private filters: FiltersDeps;
  private drfqFutureInstruments: readonly Instrument[] = [];

  constructor(deps: RfqsServiceDeps) {
    super(deps.rfqsStore);
    this.filters = deps.filters;
    const {
      productCodes,
      strategyCodes,
      auctionTypes,
      notional,
      counterparties,
    } = this.filters;
    const fetchers = [];

    if (this.shouldAddFetcher('OB')) {
      fetchers.push(deps.grfqService.listGrfqs);
    }
    if (this.shouldAddFetcher('RFQ')) {
      fetchers.push(deps.drfqService.listActiveDrfqs);
    }

    this.fetcher = new MergedSourcesFetcher<ListRfqsReq, Rfq>({
      fetchers,
      store: deps.rfqsStore,
      pageSize: 50,
      filterFn: (rfq: Rfq) =>
        isActiveRfq(rfq) &&
        hasProduct(rfq, productCodes) &&
        hasStrategy(rfq, strategyCodes) &&
        hasAuctionType(rfq, auctionTypes) &&
        hasCounterparty(rfq, counterparties) &&
        isWithinNotionalRange(rfq, notional, this.drfqFutureInstruments),
    });
  }

  setDrfqFutureInstruments(drfqFutureInstruments: readonly Instrument[]) {
    this.drfqFutureInstruments = drfqFutureInstruments;
  }

  private shouldAddFetcher(type: AuctionType) {
    return (
      this.filters.auctionTypes.some((option) => option.value === type) ||
      this.filters.auctionTypes.length === 0
    );
  }
}
